import { getIcon } from "@/lib/icons";
import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";
import { Play, X } from "lucide-react";

// Helper function to extract YouTube video ID from URL
const getYouTubeVideoId = (url) => {
  if (!url) return '';
  const match = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
  return match ? match[1] : '';
};

export const SolutionTimeline = ({ solution }) => {
  if (!solution || !solution.steps) return null;

  return (
    <div className="relative" data-testid="solution-timeline">
      {/* Overview */}
      <div className="mb-12 p-6 rounded-2xl bg-[#E6F0FA] border border-[#CCE0F5]">
        <h3 className="text-lg font-semibold text-[#0066CC] mb-2">Solution Overview</h3>
        <p className="text-slate-700">{solution.overview}</p>
      </div>

      {/* Timeline */}
      <div className="relative">
        {/* Vertical Line */}
        <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-[#0066CC] via-[#0066CC] to-slate-200" />

        {/* Steps */}
        <div className="space-y-8">
          {solution.steps.map((step, index) => (
            <motion.div
              key={step.step_number}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1, duration: 0.4 }}
              className="relative flex gap-6"
              data-testid={`timeline-step-${step.step_number}`}
            >
              {/* Step Number Circle */}
              <div className="relative z-10 w-16 h-16 rounded-full bg-white border-4 border-[#0066CC] flex items-center justify-center flex-shrink-0 shadow-lg">
                <span className="text-xl font-bold text-[#0066CC]">{step.step_number}</span>
              </div>

              {/* Step Content */}
              <div className="flex-1 pb-8">
                <div className="p-6 rounded-2xl bg-white border border-slate-100 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#E6F0FA] flex items-center justify-center text-[#0066CC]">
                        {getIcon(step.icon || "CheckCircle", "h-5 w-5")}
                      </div>
                      <h4 className="text-lg font-semibold text-slate-900">{step.title}</h4>
                    </div>
                    {step.duration && (
                      <span className="px-3 py-1 rounded-full bg-slate-100 text-slate-600 text-sm font-medium">
                        {step.duration}
                      </span>
                    )}
                  </div>
                  <p className="text-slate-600 leading-relaxed">{step.description}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export const VideoSeries = ({ videos }) => {
  if (!videos || videos.length === 0) return null;

  const hasVideos = videos.some(v => v.video_url && v.video_url.trim() !== '');
  
  if (!hasVideos) {
    return (
      <div className="text-center py-8 bg-slate-50 rounded-2xl">
        <p className="text-slate-500">Video series coming soon. Check back for solution demonstrations.</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="video-series">
      {videos.filter(v => v.video_url).map((video, index) => (
        <div key={index} className="rounded-2xl overflow-hidden bg-white border border-slate-100 shadow-sm">
          <div className="aspect-video bg-slate-900">
            <video 
              controls 
              className="w-full h-full object-cover"
              poster=""
              data-testid={`video-player-${index}`}
            >
              <source src={video.video_url} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
          <div className="p-4">
            <h4 className="font-semibold text-slate-900">{video.title || `Video ${index + 1}`}</h4>
          </div>
        </div>
      ))}
    </div>
  );
};

// YouTube Video Gallery with thumbnails and modal playback
export const YouTubeVideoGallery = ({ youtubeUrls, youtubeTitles = [] }) => {
  const [selectedVideo, setSelectedVideo] = useState(null);

  // Filter out empty URLs and get valid video IDs with titles
  const validVideos = (youtubeUrls || [])
    .map((url, index) => ({
      url,
      title: youtubeTitles[index] || `Video ${index + 1}`,
      videoId: getYouTubeVideoId(url)
    }))
    .filter(v => v.url && v.url.trim() && v.videoId);

  if (validVideos.length === 0) return null;

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="youtube-video-gallery">
        {validVideos.map((video, index) => (
          <motion.button
            key={index}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: index * 0.1 }}
            onClick={() => setSelectedVideo(video)}
            className="group relative rounded-xl overflow-hidden bg-slate-900 border-2 border-transparent hover:border-[#0066CC] transition-all shadow-md hover:shadow-xl"
            data-testid={`youtube-thumbnail-${index}`}
          >
            <div className="aspect-video">
              <img
                src={`https://img.youtube.com/vi/${video.videoId}/mqdefault.jpg`}
                alt={video.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
              <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                <div className="w-12 h-12 rounded-full bg-red-600 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                  <Play className="w-6 h-6 text-white ml-1" fill="white" />
                </div>
              </div>
            </div>
            {/* Video Title */}
            <div className="p-3 bg-white">
              <p className="text-sm font-medium text-slate-900 line-clamp-2">{video.title}</p>
            </div>
          </motion.button>
        ))}
      </div>

      {/* Video Modal */}
      <AnimatePresence>
        {selectedVideo && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
            onClick={() => setSelectedVideo(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="relative w-full max-w-4xl bg-black rounded-2xl overflow-hidden shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setSelectedVideo(null)}
                className="absolute -top-12 right-0 p-2 text-white hover:text-red-400 transition-colors z-10"
                data-testid="close-video-modal"
              >
                <X className="w-8 h-8" />
              </button>
              <div className="aspect-video">
                <iframe
                  src={`https://www.youtube.com/embed/${selectedVideo.videoId}?autoplay=1&rel=0`}
                  title={selectedVideo.title}
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
              {/* Video Title in Modal */}
              <div className="p-4 bg-slate-900">
                <h3 className="text-white font-semibold">{selectedVideo.title}</h3>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
